import React from 'react';
import './Features.css';

interface Feature {
  icon: string;
  title: string;
  description: string;
  benefits: string[];
  color: string;
}

const Features: React.FC = () => {
  const features: Feature[] = [
    {
      icon: '⚡',
      title: 'Real-Time Vibe Score',
      description: 'A live score for every venue based on crowd-sourced data — updated every few minutes by people who are already there.',
      benefits: ['Foot traffic density', 'Noise level & energy', 'Queue length at the door', 'Drink wait time crowdsourced'],
      color: '#FF2DAA'
    },
    {
      icon: '🗺️',
      title: 'Live City Heatmap',
      description: 'See the whole city breathe in real time. The map glows to show you exactly where the energy is right now.',
      benefits: ['Red = packed & popping', 'Yellow = decent crowd', 'Blue = dead tonight', 'Updates every 10 minutes'],
      color: '#00C2FF'
    },
    {
      icon: '🗳️',
      title: 'Anonymous Crowd Polls',
      description: 'Every 10 minutes, users at a venue get a 1-tap poll that keeps the feed live, accurate, and addictively fresh.',
      benefits: ['"Is the DJ fire right now?"', '"Is the line moving?"', '"Mostly guys or girls?"', '"Worth pulling up?"'],
      color: '#A020F0'
    },
    {
      icon: '👥',
      title: 'Friends Ghost Mode',
      description: 'See where your squad is heading without being creepy. Three modes so everyone stays comfortable.',
      benefits: ['Public — show your location', 'Ghost — fully hidden', 'Signal — venue only visible', 'Zero pressure, all fun'],
      color: '#FF2DAA'
    },
    {
      icon: '📲',
      title: 'Auto TikTok Clips',
      description: 'The app auto-generates shareable city nightlife recaps that clubs and people will repost instantly.',
      benefits: ['"Tonight\'s Vibe in Milwaukee"', '"Top 3 Lit Spots Right Now"', '"Where Everyone\'s Going"', 'Clubs repost = free growth'],
      color: '#00C2FF'
    },
    {
      icon: '🏢',
      title: 'Venue Integration',
      description: 'Bars and clubs get a direct line to their crowd — push deals, update wait times, and attract more foot traffic.',
      benefits: ['Push bottle specials live', 'Update line wait time', 'Promote DJs & events', '"Line moving fast" alerts'],
      color: '#A020F0'
    }
  ];

  return (
    <section id="features" className="features section" aria-labelledby="features-heading">
      <div className="container">
        <div className="features-header">
          <div className="section-badge">🔥 Why LITSCAN</div>
          <h2 id="features-heading">Built for the Night</h2>
          <p className="features-subtitle">
            One screen. Real-time data. Zero guessing. LITSCAN gives you the inside scoop
            on every bar, club, and lounge — before you waste a Lyft to find out it's dead.
          </p>
        </div>

        <div className="features-grid grid grid-3">
          {features.map((feature, index) => (
            <article key={index} className="feature-card" style={{ '--card-accent': feature.color } as React.CSSProperties}>
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
              <ul className="feature-benefits">
                {feature.benefits.map((benefit, i) => (
                  <li key={i} className="feature-benefit">
                    <span className="benefit-icon">▸</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
